import React from 'react';
import {ChartType} from '@/views/storyboard/utils/chartConstants';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {DateTime} from 'luxon';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';
import {Tooltip} from 'components/tooltips';

interface Props {
    value: string;
    comparableValue?: number | null;
    type?: ChartType;
    disclaimer?: boolean;
}

const TrendValueString = React.memo<Props>(React.forwardRef<HTMLSpanElement, Props>((
    {
        value,
        type,
        comparableValue,
        disclaimer = true,
    },
    ref
) => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    if (loadingUserData || !userData) {
        return <span ref={ref} style={{marginLeft: 4}}>{value}%</span>;
    }

    const dateRangeEnd = DateTime.fromFormat(userData?.sessionEndDate || '', 'dd/MM/yyyy');
    const today = DateTime.now().startOf('day');
    const includesToday = dateRangeEnd.isValid && dateRangeEnd.hasSame(today, 'day');

    const hasComparable = typeof comparableValue === 'number' && !Number.isNaN(comparableValue);
    const showDisclaimer = includesToday && disclaimer;
    const numericValue = parseFloat(value);
    const hasNumeric = !Number.isNaN(numericValue);
    const isPositive = hasNumeric && numericValue > 0;
    const isZero = hasNumeric && numericValue === 0;
    const disclaimerKey = 'es_period_includes.text';

    // Build single tooltip content: comparable value (if present) + optional disclaimer line.
    const tooltipContent = (
        <>
            {hasComparable && (
                <>
                    {i18n.t('tt_comparable_value.text')} {comparableValue}
                </>
            )}
            {hasComparable && showDisclaimer && <br />}
            {showDisclaimer && i18n.t(disclaimerKey)}
        </>
    );

    const showTooltip = hasComparable || showDisclaimer;

    const inner = (
        <span
            style={{
                color: isPositive ? '#1A8F5E' : !isZero ? 'red' : undefined,
                marginLeft: 4,
                fontWeight: 400,
            }}
            className='d-flex align-items-center mb-0'
        >
            {!isZero && (
                <span
                    aria-hidden="true"
                    className={type === ChartType.BigNumber && !isPositive ? 'pr-1' : ''}
                    style={{lineHeight: 1, marginRight: 3}}
                >
                    {isPositive ? '▲' : '▼'}
                </span>
            )}
            <span>{value}%</span>
            {showDisclaimer && (
                <span style={{marginLeft: 4}} className='d-flex align-items-center'>
                    <Icon
                        name="mdi-information-outline"
                        size="1em"
                        style={{color: '#8a6d1d'}}
                    />
                </span>
            )}
        </span>
    );

    return (
        <span ref={ref}>
            {showTooltip ? (
                <Tooltip content={tooltipContent} placement="bottom">
                    {inner}
                </Tooltip>
            ) : (
                inner
            )}
        </span>
    );
}));

export default TrendValueString;
